import { SOURCE_UNREAD } from './portal/display_strings.ts';
// The review surface's single vocabulary source (2026-08-08 iteration).
//
// Two people who know this domain cold — including the person who WROTE the
// medication-grouping brief — looked at "Type C · Standard" on screen and
// could not say what it meant. The letters A/B/C are an internal taxonomy
// code; lawyers get plain words, and every term gets a definition that hover
// (title=) and the Definitions tab both draw from. One module so a label and
// its definition can never drift apart.

// Explicit extension: scripts/telehealth-binder.test.mjs loads this module in
// plain Node, which resolves no extension-less import.
import { RECORDS_CAPACITY_LABEL } from './customer/strings/records-access.ts';
import { RULE_PLAIN } from './shared/rulePlain.ts';
import type { RuleGroupRow } from './api/types.ts';

export const MED_TYPE_ORDER = ['controlled', 'compounded', 'standard'] as const;
export type MedType = (typeof MED_TYPE_ORDER)[number];

export const OPERATOR_REMINDER_METHOD = {
  copied: 'Copied by the team (delivery not confirmed)',
  opened: "Opened in the team's email app (delivery not confirmed)",
  sent: 'Sent by the email service',
} as const;

export const MED_TYPE_LABEL: Record<string, string> = {
  controlled: 'Controlled substances',
  compounded: 'Compounded medications',
  standard: 'Standard (non-controlled) medications',
};

export const MED_TYPE_DEFINITION: Record<string, string> = {
  controlled:
    'DEA-scheduled drugs (testosterone, ketamine, stimulants, and similar). ' +
    'Prescribing them requires DEA registration per provider per state, and ' +
    'many states add their own controlled-substance registration, PDMP check, ' +
    'or exam requirements on top.',
  compounded:
    'Medications prepared by a compounding pharmacy rather than bought as an ' +
    'FDA-approved finished product (compounded GLP-1s, peptides, hormone ' +
    'preparations). FDA 503A/503B sourcing rules apply; no DEA registration ' +
    'is needed unless the drug is also scheduled.',
  standard:
    'Non-scheduled medicines; FDA approval is a separate product fact. State licensure and ' +
    'telehealth practice rules apply; no DEA registration is required. Some ' +
    'standard drugs can also be compounded, which is noted where it matters.',
};

// Glossary for the Definitions tab and hover-overs. Keys render as the term;
// keep the language customer-plain (no engine tokens — the display-strings
// gate watches portal surfaces, and this module holds the review side to the
// same standard by hand).
export const TERM_DEFINITIONS: Record<string, string> = {
  'Controlled substances': MED_TYPE_DEFINITION.controlled,
  'Compounded medications': MED_TYPE_DEFINITION.compounded,
  'Standard (non-controlled) medications': MED_TYPE_DEFINITION.standard,
  'DEA schedule':
    'The federal classification (II–V) of a drug with abuse potential. A ' +
    'scheduled drug is a controlled substance; the schedule number drives ' +
    'which prescribing rules attach.',
  'State CS registration (CSR)':
    'A separate state-level controlled-substance registration some states ' +
    'require in addition to the federal DEA registration.',
  'PDMP':
    "A state's Prescription Drug Monitoring Program — the database many " +
    'states require prescribers to check before writing a controlled ' +
    'prescription.',
  'EPCS':
    'Electronic Prescribing of Controlled Substances — the certified ' +
    'e-prescribing standard many states mandate for scheduled drugs.',
  '503A / 503B':
    'The two federal compounding pathways: 503A traditional pharmacies ' +
    '(patient-specific prescriptions) and 503B outsourcing facilities ' +
    '(office stock). Which pathway a compounded drug may use is a federal ' +
    'question that state rules build on.',
  'Bona fide exam / good-faith exam':
    'The examination or provider-patient relationship a state requires ' +
    'before prescribing. States differ on whether a telehealth encounter — ' +
    'or an online questionnaire alone — can establish it.',
  'Corporate practice of medicine (CPOM)':
    'A state doctrine restricting who may own or control a medical ' +
    'practice. Where it is strong, business-structure requirements (like a ' +
    'real in-state office of record) attach regardless of what is ' +
    'prescribed.',
  Remediation:
    'A requirement the supplied records do not meet (such as a missing licence, a ' +
    'missing DEA registration, a drug prescribed where it may not be). ' +
    'The binder names the recorded fact and what the practice needs to change.',
  Confirmation:
    'A requirement that depends on how the practice works, such as an exam ' +
    'or database check that its records cannot show. It is not a failure. ' +
    'It remains open until the practice confirms in writing what it does. ' +
    'Reviewers may ask for supporting records.',
  'Settled finding':
    'A finding is settled when every failing rule on it has a decision. A defer settles it only when no failing rule remains undecided.',
  'Deferred finding':
    'A finding is counted as deferred when at least one failing rule is deferred. It may still be not settled if another failing rule needs a decision.',
  'Rule decision':
    'One decision on a rule for the findings it covers. A finding may need decisions on more than one failing rule before it is settled.',
};

// The med-spa review's own glossary (2026-09-07). The first med-spa binder's
// Definitions tab showed DEA schedules, 503A/503B and PDMP to counsel reviewing
// chemical peels and laser hair removal; a med-spa review turns on who may
// perform a service, under what supervision, and on what the practice stated.
export const MEDSPA_TERM_DEFINITIONS: Record<string, string> = {
  'Permitted / not permitted':
    "Whether the state's own rule lets this provider role perform this " +
    'service. Only a primary source — the board rule, statute or register — ' +
    'closes a service to a role; the citation beside the finding is that rule.',
  'Permitted with conditions':
    'The state allows the service for this role only on a condition the ' +
    'practice has to meet: training, a supervising or delegating prescriber, ' +
    "a prescriber's examination first, a stated scope limit, a separate " +
    'credential, or a written protocol. The condition is quoted from the rule.',
  "The practice's statements":
    'The answers the practice gave in its account — who oversees whom, ' +
    'training completed, operating within a limit, licence status. A ' +
    'compliant finding rests on them and says so; a statement of "no" closes ' +
    'the finding, and an unanswered one leaves it open.',
  Provisional:
    "Meets requirements based on the practice's own statements. VerityRx has not " +
    'verified those statements with a board; licence status is self-reported. ' +
    'Review these findings more closely.',
  'Supervision level':
    'What the state requires of the prescriber behind a delegated service: ' +
    'on site while it is performed, contactable but elsewhere, or a written ' +
    'practice arrangement with a physician (for a nurse practitioner or ' +
    'physician assistant). The finding names which one applies.',
  'Good-faith exam':
    "A prescriber's examination of the patient before a delegated treatment " +
    "— in person or over live video, depending on the state. The practice " +
    'states who performs it and when.',
  'Scope limit':
    'The boundary inside which the state permits the service for this role ' +
    '(for example, superficial peels only). Quoted from the rule; the ' +
    'practice states that it operates within it.',
  'Attorney review flag':
    'A cited answer our lawyer still needs to confirm because sources conflict ' +
    'or the reading is contested. The flag does not change the answer shown.',
  'Needs your answer':
    'The result waits on a fact only the practice can give: a yes, a no or a ' +
    'file. Reported as open, never as permitted.',
  'Not yet reviewed by us':
    'VerityRx has not completed a verified reading for this service and role ' +
    'in this state. Reported as open, never as permitted.',
  'Unresolved in law':
    'VerityRx read the state\'s sources and they leave the point open, or ' +
    'point different ways. It is marked for a lawyer and reported as open, ' +
    'never as permitted.',
  'This state does not license this role':
    'The state issues no licence for this role, so the role has no scope of ' +
    'its own there. Stated from the state\'s licence roster, not from a silence ' +
    'in its law. Reported as open, never as permitted or prohibited: the ' +
    'state may license the work under another name.',
  Remediation: TERM_DEFINITIONS.Remediation,
  Confirmation: TERM_DEFINITIONS.Confirmation,
};

export function termDefinitions(vertical: string | null | undefined): Record<string, string> {
  return vertical === 'medspa_service' ? MEDSPA_TERM_DEFINITIONS : TERM_DEFINITIONS;
}

// Plain words for the two verticals and the round's status — wire tokens
// ("telehealth_rx", "in_review") were on the queue cards and in the switcher.
export const VERTICAL_LABEL: Record<string, string> = {
  telehealth_rx: 'Telehealth prescribing',
  medspa_service: 'Med spa',
};
export function verticalLabel(v: string | null | undefined): string {
  return v ? (VERTICAL_LABEL[v] ?? v) : '';
}

// A round the reviewer has not touched reads "Not started", never "Draft":
// "Draft" on a binder that was already sent for review read as "not final /
// not sent" (COHERE2 P0-6).
export const STATUS_LABEL: Record<string, string> = {
  draft: 'Not started',
  in_review: 'In review',
  completed: 'Review complete',
  finalized: 'Finalized',
  // PMWALK PM-28 (migration 0210): the team sent a newer binder and closed
  // this round; counsel's decisions in it carry forward.
  superseded: 'Replaced by a newer binder',
};
export function statusLabel(s: string | null | undefined): string {
  return s ? (STATUS_LABEL[s] ?? s.replace(/_/g, ' ')) : '';
}

// R9 (2026-09-29): a narrow-scope round ("Only what needs a legal judgement")
// asks counsel to sign off on a SUBSET of the report, so the sign-off wording
// says which subset -- "Signed off, N findings undecided" alone would read as
// the whole report when only the legal-judgement groups were ever counsel's.
export function signedOffLabel(
  unreviewed: number | null | undefined,
  scope?: string | null,
  scopedTotal?: number | null,
): string {
  if (scope === 'legal_judgement' && !unreviewed && scopedTotal != null) {
    return `Signed off: the ${scopedTotal} ${scopedTotal === 1 ? 'item' : 'items'} that needed a legal judgement`;
  }
  if (!unreviewed) return 'Signed off';
  return `Signed off with ${unreviewed} ${unreviewed === 1 ? 'finding' : 'findings'} undecided`;
}

// Round 2 (Arvind 2026-09-29 decision): a narrow round must say who cleared
// the settled findings and when, not just that some were left out. The exact
// sentence this renders is the one named in the decision, and it appears in
// three places: the counsel console's scope note, the sign-off banner, and
// the Account page -- all three call this one function so they can never say
// three different things about the same round.
/** What a narrow round left out, by who owns it (PMWALK CN-2). */
export interface SettledKinds {
  awaiting_practice: number;
  not_met: number;
  confirmations: number;
  compliant: number;
  /** Greys an older binder never classified (C3SCOPE). */
  not_classified?: number;
  source_access?: number;
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "463 wait on the practice's answers, 9 are requirements not met on
 *  recorded facts" -- only the non-zero parts. */
export function settledBreakdown(k: SettledKinds): string {
  return [
    k.awaiting_practice > 0 && `${count(k.awaiting_practice, 'question waits', 'questions wait')} on the practice's answers`,
    k.not_met > 0 && `${count(k.not_met, 'is a requirement', 'are requirements')} not met on recorded facts`,
    k.confirmations > 0 && `${count(k.confirmations, 'is a confirmation', 'are confirmations')} the practice owes`,
    k.compliant > 0 && `${k.compliant} ${k.compliant === 1 ? 'is' : 'are'} compliant`,
    (k.source_access ?? 0) > 0 && `${k.source_access} await a readable official source`,
    (k.not_classified ?? 0) > 0 && `${k.not_classified} ${k.not_classified === 1 ? 'is' : 'are'} not classified (an older binder)`,
  ].filter(Boolean).join('; ');
}

export function scopeSummarySentence(opts: {
  scope?: string | null;
  legalJudgementCount?: number | null; // N -- what counsel reviewed
  settledCount?: number | null; // M -- what was already settled
  settledConfirmedByEmail?: string | null;
  settledConfirmedAt?: string | null; // ISO
  // PMWALK CN-2: when known, the other M said by who owns them, so the
  // practice's unanswered questions never read as "reviewed".
  settledKinds?: SettledKinds | null;
  // BL-803: the sentence states the scope -- what counsel is asked -- in the
  // present tense until the round is complete; "reviewed" only once it is.
  roundComplete?: boolean;
}): string | null {
  if (opts.scope !== 'legal_judgement') return null;
  const n = opts.legalJudgementCount ?? 0;
  const nWord = n === 1 ? 'item' : 'items';
  const base = opts.roundComplete
    ? `Counsel reviewed the ${n} ${nWord} that needed a legal judgement`
    : `Counsel is asked to review the ${n} ${nWord} that ${n === 1 ? 'needs' : 'need'} a legal judgement`;
  const m = opts.settledCount ?? 0;
  if (m <= 0) return `${base}.`;
  const mWord = m === 1 ? 'finding' : 'findings';
  const date = opts.settledConfirmedAt
    ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(opts.settledConfirmedAt))
    : null;
  if (opts.settledKinds) {
    const who = opts.settledConfirmedByEmail && date
      ? ` ${opts.settledConfirmedByEmail} confirmed on ${date} that none of them needs a legal judgement.`
      : '';
    return `${base}. Of the other ${m}: ${settledBreakdown(opts.settledKinds)}.${who}`;
  }
  if (!opts.settledConfirmedByEmail || !date) {
    return `${base}; the other ${m} ${mWord} ${m === 1 ? 'is' : 'are'} not yet confirmed reviewed.`;
  }
  return `${base}; ${opts.settledConfirmedByEmail} reviewed the other ${m} on ${date}.`;
}

export function roundStatusLabel(
  status: string | null | undefined,
  outcome?: string | null,
  unreviewed?: number | null,
  scope?: string | null,
  scopedTotal?: number | null,
  progress?: { reviewed: number; total: number } | null,
): string {
  if (status && ['completed', 'finalized'].includes(status)) {
    if (outcome === 'signed_off') return signedOffLabel(unreviewed, scope, scopedTotal);
    if (outcome === 'changes_requested') return 'Changes requested';
  }
  // One state list (COHERE2 P0-6): Not started / In review (N of M decided) /
  // Changes requested / Signed off. A draft round with decisions recorded is
  // in review, and the count travels with the words.
  if (status === 'draft' || status === 'in_review') {
    const decided = progress?.reviewed ?? 0;
    if (progress && progress.total > 0 && (status === 'in_review' || decided > 0)) {
      return `In review (${decided} of ${progress.total} decided)`;
    }
    return status === 'in_review' ? 'In review' : 'Not started';
  }
  return statusLabel(status);
}

export function medTypeLabel(t: string | null | undefined): string {
  if (!t) return 'Services / unclassified';
  return MED_TYPE_LABEL[t] ?? t;
}

export function medTypeDefinition(t: string | null | undefined): string | undefined {
  return t ? MED_TYPE_DEFINITION[t] : undefined;
}

// Who someone is, in words a teammate or a lawyer would use. 'internal_admin'
// is a role name from the auth layer; nobody should read it on a screen.
export const ROLE_LABEL: Record<string, string> = {
  attorney: 'Reviewer',
  customer: 'The practice',
  customer_admin: "The practice's admin",
  customer_records: RECORDS_CAPACITY_LABEL,
  customer_readonly: 'The practice (view only)',
  internal_admin: 'Team',
};
// The grant roles that open a practice's own workspace (deps.py
// PRACTICE_GRANT_ROLES). Every routing decision keyed on "is this person the
// practice" goes through here, so a new practice capacity is added once.
export function isPracticeRole(r: string | null | undefined): boolean {
  return r === 'customer' || r === 'customer_admin' || r === 'customer_records' ||
    r === 'customer_readonly';
}
export function roleLabel(r: string | null | undefined): string {
  return (r && ROLE_LABEL[r]) || (r ?? '').replace(/_/g, ' ');
}

// The engine's severity tier, as a reviewer reads it. `informational` is the
// resting state of an ordinary finding and says nothing worth a chip, so it
// renders as nothing; the raw token (`highest_risk`) reached the finding line
// until 2026-09-07.
const TIER_LABEL: Record<string, string> = {
  highest_risk: 'highest risk',
  moderate: 'moderate risk',
};
export function severityTierLabel(t: string | null | undefined): string {
  if (!t || t === 'informational') return '';
  return TIER_LABEL[t] ?? t.replace(/_/g, ' ');
}

// A CONFIRMATION group is an operational attestation, not a finding — the
// board's own heading says "your role is to agree the rule applies", and a
// verdict-shaped Accept/Reject/Defer offered "Reject, correct verdict: Needs
// review" for a rule counsel disagrees is even the right proposition
// (LR-REPORT.md F.3). These are its own verbs, stored as their own
// disposition types (migration 0130) so a confirmation reads honestly on
// every surface that lists dispositions.
export const CONFIRMATION_VERBS = {
  applies: { type: 'confirm_applies' as const, label: 'Rule applies' },
  notApplicable: {
    type: 'confirm_not_applicable' as const,
    label: 'Rule does not apply',
  },
} as const;

// Plain titles for the few rules whose register title reads ambiguously to a
// lawyer. (Not RULE_PLAIN: that map is the practice's 57 KB of wording and
// belongs to the practice's chunk, not the review console's.)
const COUNSEL_RULE_TITLE: Record<string, string> = Object.fromEntries(
  ['auth_ky_aprn_capa_ns_or_exemption', 'auth_ky_aprn_capa_cs_or_exemption'].map((code) => [
    code,
    RULE_PLAIN[code].title,
  ]),
);

// P1-18 (C3WORDS): the shorthand a rule title may open with, spelled out. Only
// at the START of a title (or a clause), where it names the person the rule is
// about; "PA" elsewhere in a sentence may be a state.
const ROLE_SHORTHAND: [RegExp, string][] = [
  [/^PA (?=prescrib|supervis|collaborat|agreement)/, 'Physician assistant '],
  [/^NP (?=prescrib|still|in a |needs|must)/, 'Nurse practitioner '],
  [/^APRN (?=prescrib|must|needs)/, 'Advanced practice nurse '],
  [/^APRN\b/, 'Advanced practice nurse'],
];

/** The one display helper for the noun's spelling (C3FOLLOW): register text keeps its
 *  American "license" (never edited for spelling alone) and every surface renders "licence". */
export function licenceSpelling(text: string): string {
  return text.replace(/\blicense(s?)\b/g, 'licence$1').replace(/\bLicense(s?)\b/g, 'Licence$1');
}

/** A rule group's label as counsel reads it (COHERE2 P1-28): "PA" is spelled
 *  out (Pennsylvania or physician assistant was ambiguous next to
 *  "Pennsylvania CRNP ..."), and a short title-style label of a single rule
 *  takes that rule's plain title ("Kentucky CAPA-NS or statutory exemption").
 *  Full-sentence labels are counsel's own wording and are left alone, except that
 *  the noun is spelled "licence" as everywhere else (C3PAGES P2-2). */
export function plainRuleLabel(label: string, codes?: readonly string[] | null): string {
  // Legacy snapshots may have no rule title or summary, so the importer can
  // only send the stable code. Never let that join key become counsel copy.
  if (/\b[a-z][a-z0-9]*(?:_[a-z0-9]+){2,}\b/i.test(label)) {
    return 'Rule details unavailable';
  }
  const plain = codes && codes.length === 1 ? COUNSEL_RULE_TITLE[codes[0]] : undefined;
  if (plain && label.length <= 110 && !/[.;:]/.test(label)) return plain;
  let out = label;
  for (const [re, to] of ROLE_SHORTHAND) out = out.replace(re, to);
  // One spelling of the noun across every surface (COHERE3 P2-2).
  return licenceSpelling(out);
}

/** A rule's grey findings in the tiles' words (P1-2): "waiting on the
 *  practice" and "need your legal judgement", never "open questions". A rule
 *  from a server that sends no split says only that they are undecided. */
export function greyParts(group: Pick<RuleGroupRow, 'verdict_counts' | 'grey_kinds'>): string[] {
  const grey = group.verdict_counts.grey;
  if (grey <= 0) return [];
  const k = group.grey_kinds;
  if (!k) return [`${grey} undecided`];
  const practice = k.awaiting_practice + k.awaiting_confirmation;
  const counsel = k.legal_judgement + k.unknown;
  return [
    (k.awaiting_source ?? 0) > 0 && `${k.awaiting_source} — ${SOURCE_UNREAD}`,
    practice > 0 && `${practice} waiting on the practice`,
    counsel > 0 && `${counsel} ${counsel === 1 ? 'needs' : 'need'} your legal judgement`,
  ].filter((p): p is string => !!p);
}

/** The grey class named by who closes it, in the tiles' words, for a button
 *  ("Accept 3 waiting on the practice"). One word for one kind; both kinds
 *  named together; a server that sends no split says only "undecided". */
export function greyClassWords(group: Pick<RuleGroupRow, 'grey_kinds'> | undefined): string {
  const k = group?.grey_kinds;
  if (!k) return 'undecided';
  const practice = k.awaiting_practice + k.awaiting_confirmation;
  const counsel = k.legal_judgement + k.unknown;
  if ((k.awaiting_source ?? 0) > 0) return SOURCE_UNREAD;
  if (practice > 0 && counsel === 0) return 'waiting on the practice';
  if (counsel > 0 && practice === 0) return 'needing your legal judgement';
  return 'waiting on the practice or needing your legal judgement';
}

/** Which of the board's rule chips a (non-confirmation) rule counts under. */
export function ruleChipKind(g: Pick<RuleGroupRow, 'verdict_counts' | 'grey_kinds'>): 'not_met' | 'source' | 'practice' | 'judgement' | 'undecided' | null {
  if (g.verdict_counts.non_compliant > 0) return 'not_met';
  if (g.verdict_counts.grey <= 0) return null;
  const k = g.grey_kinds;
  if (!k) return 'undecided';
  if ((k.awaiting_source ?? 0) > 0) return 'source';
  return k.legal_judgement + k.unknown > 0 ? 'judgement' : 'practice';
}

/** What counsel decided, in decisions: "4 decisions: 2 accepted · 1 rejected · 1 deferred · 2 comments".
 *  A finding resolves only when every rule failing it is decided, so counting findings read
 *  "0 accepted" for a round with decisions in it (PD-5). A round completed before decisions were
 *  counted falls back to its finding counts, said as findings. */
export function decisionsLine(summary: {
  accept?: number;
  reject?: number;
  defer?: number;
  decisions?: { accept: number; reject: number; defer: number; comments: number; findings_covered?: number };
}): string {
  const d = summary.decisions;
  if (!d) {
    const accepted = summary.accept ?? 0;
    const rejected = summary.reject ?? 0;
    const deferred = summary.defer ?? 0;
    return `${accepted} accepted · ${rejected} rejected · ${deferred} deferred findings`;
  }
  const n = d.accept + d.reject + d.defer;
  const parts = [`${d.accept} accepted`, `${d.reject} rejected`, `${d.defer} deferred`];
  if (d.comments > 0) parts.push(`${d.comments} comment${d.comments === 1 ? '' : 's'}`);
  const decisions = `${n} rule decision${n === 1 ? '' : 's'}`;
  const coverage = d.findings_covered;
  const coverageWords = coverage === undefined
    ? decisions
    : `${decisions} covering ${coverage} ${coverage === 1 ? 'finding' : 'findings'}`;
  return `${coverageWords}: ${parts.join(' · ')}`;
}

export function findingCountsLine(accepted: number, rejected: number, deferred: number, undecided: number): string {
  return `${accepted} accepted · ${rejected} rejected · ${deferred} deferred · ${undecided} undecided findings`;
}

/** The findings no decision has settled yet, said as findings (never as decisions). */
export function undecidedFindingsLine(n: number): string {
  return n > 0 ? `${n} finding${n === 1 ? '' : 's'} not settled by a decision` : '';
}
